// src/hooks/useChat.js
// FIXED:
//   - Tool status cards no longer duplicated across loop iterations
//   - Line breaks preserved in streamed assistant text

import { inferRecordTypeFromFileName } from '../constants/recordTypes';
import { useState, useCallback, useRef, useEffect } from 'react';
import { sendChatMessage, buildTriageContext } from '../services/chatService';
import { useAutoTriage, isScanImage } from '../components/healthrecords/useAutoTriage';
import { healthRecordAPI } from '../services/api';
import { persistsWhenDone } from '../constants/chatToolLabels';
import { translate } from '../services/chatTranslateAPI';
import { chatLanguage } from '../utils/chatLanguage';

// Helpers for the tool status pills. A pill is created when a step starts and
// must be resolved when it finishes — a pill left in 'running' animates
// forever, which is what left "Getting your panel details…" bouncing under the
// panels it had already fetched.
//
// Resolving means one of two things, decided per action in chatToolLabels.js: a
// step whose result is already visible (any read, and the writes that render
// their own payment card) drops its pill entirely, because the answer on screen
// is the receipt; a write with no other receipt keeps its pill so the patient
// can see their record was changed. A FAILED step always keeps its pill —
// silently removing one is telling the patient it worked.
function settleStatus(ts, outcome) {
  if (outcome === 'error') return { ...ts, status: 'error' };
  return persistsWhenDone(ts.toolName, ts.action) ? { ...ts, status: 'done' } : null;
}

function settleToolStatuses(message, outcome = 'done') {
  if (!message?.toolStatuses?.length) return message;
  if (!message.toolStatuses.some(ts => ts.status === 'running')) return message;
  return {
    ...message,
    toolStatuses: message.toolStatuses
      .map(ts => (ts.status === 'running' ? settleStatus(ts, outcome) : ts))
      .filter(Boolean),
  };
}

// Settle ONE step — the oldest still-running pill for this tool and action.
// Matching on the action too matters now that a single turn can run several
// actions of the same tool (read the profile, read the addresses, save one),
// each with its own pill.
function settleOneToolStatus(message, toolName, action, outcome) {
  const statuses = message?.toolStatuses;
  if (!statuses?.length) return message;
  const idx = statuses.findIndex(ts =>
    ts.status === 'running' && ts.toolName === toolName && ts.action === action);
  if (idx === -1) return message;
  const settled = settleStatus(statuses[idx], outcome);
  return {
    ...message,
    toolStatuses: settled
      ? statuses.map((ts, i) => (i === idx ? settled : ts))
      : statuses.filter((_, i) => i !== idx),
  };
}

// Claude sometimes narrates before it calls a tool ("Let me pull up the panel
// options…"). That narration is worth keeping — it just must not be sitting in
// the bubble the post-tool answer streams into, or it gets overwritten and the
// patient sees a message appear and vanish. What is NOT worth keeping is the
// bracketed pseudo-narration it occasionally emits instead ("[Calling
// get_panel_pricing]"), which is the reason this text used to be discarded
// wholesale.
const TOOL_NARRATION_ONLY = /^\[[^\]]*\]$/;

function isKeepableNarration(text) {
  const trimmed = (text ?? '').trim();
  return trimmed.length > 0 && !TOOL_NARRATION_ONLY.test(trimmed);
}

// Strip ALL XML tool narration from text_delta — never show to patient.
// Claude leaks various XML tag formats depending on model version:
//   <function_calls>, <invoke>, <function_calling_result>  ← older format
//   <tool_call>, <tool_response>, </tool_call>, </tool_response>  ← newer format
//   Raw JSON parameter dumps
// Strategy: once ANY XML tag is detected, cut from that point to end of stream.
function stripToolXml(accumulated) {
  const xmlStartIdx = accumulated.search(
    /<function_calls>|<invoke |<function_calling_result>|<tool_call|<tool_response|<\/tool_|<function_calls>|<invoke/
  );
  return xmlStartIdx > -1
    ? accumulated.slice(0, xmlStartIdx).trim()  // keep only text before XML starts
    : accumulated;
}

// A reply bubble once its "out" translation has come back. Only the bubble's
// text changes — never a card, a tool pill, or the English in apiMessages.
// ok: the translation, with the English and any safety-check flags kept so
// the patient can check it. disabled: the English, exactly as today.
// Anything else (fallback, an error, a timeout): the English plus a short note
// in the patient's language saying it couldn't be translated.
function withTranslation(message, english, result, lang) {
  const settled = { ...message, streaming: false, awaitingTranslation: false };
  if (result.ok) {
    return { ...settled, text: result.text, english, flags: result.flags ?? [], translationLang: lang };
  }
  if (result.disabled) return { ...settled, text: english };
  return { ...settled, text: english, translationFailed: true, translationLang: lang };
}

export function useChat(patient, triageContext) {
  // Keep triageContext in a ref so sendMessage always reads the latest value.
  // Without this, the useCallback closure captures triageContext at hook creation
  // time — if welcomeSummary hasn't loaded yet (first render), the chat request
  // goes with null context even if the summary loaded before the user sent.
  const triageContextRef = useRef(triageContext);
  useEffect(() => { triageContextRef.current = triageContext; }, [triageContext]);
  const [apiMessages, setApiMessages]       = useState([]);
  const [displayMessages, setDisplayMessages] = useState([]);
  const [isLoading, setIsLoading]           = useState(false);
  const [error, setError]                   = useState(null);

  // ── Chat-based upload (Sprint 15) ──────────────────────────────────────────
  // Reuses the exact same pipeline as the dedicated /patient/upload page
  // (useAutoTriage) rather than reimplementing extraction/Claude/RF here.
  // The file itself never goes through the Claude conversation — it's a
  // normal multipart upload to HealthRecordService, then a follow-up chat
  // turn (with fresh triageContext) so Claude can acknowledge the result.
  const { stage: uploadStage, triageError: uploadError, runAutoTriage } = useAutoTriage();
  const uploadIdRef = useRef(null);
  // Files staged after 📎 attach, waiting on the patient to pick a document
  // type (document_type_picker widget) before the actual upload fires.
  // Keyed by pickerId — a File object can't safely live in React state
  // (not serializable, and we don't want re-renders holding onto it), so a
  // ref-backed Map is the right tool here.
  const pendingFilesRef = useRef(new Map());

  // Ask Previu in Malayalam / Hindi. Once the server says translation is
  // switched off, stop asking for the rest of this conversation.
  const translateOffRef = useRef(false);
  // Ties each reply bubble to its "out" translation when that lands.
  const bubbleSeqRef = useRef(0);

  // Mirror useAutoTriage's stage into whichever upload_status widget message
  // is currently active. uploadIdRef (not array index) survives re-renders
  // and concurrent widget insertions safely.
  useEffect(() => {
    if (!uploadIdRef.current) return;
    setDisplayMessages(prev => prev.map(m =>
      m.role === 'upload_status' && m.uploadId === uploadIdRef.current
        ? { ...m, stage: uploadStage, errorText: uploadStage === 'FAILED' ? uploadError : undefined }
        : m
    ));
  }, [uploadStage, uploadError]);

  const sendMessage = useCallback(async (text, contextOverride) => {
    if (!text.trim() || isLoading) return;
    setError(null);

    // Malayalam / Hindi (script, or Manglish / Hinglish in Latin letters).
    // null for English — then nothing below differs from a plain turn.
    const language = translateOffRef.current ? null : chatLanguage(text);

    // Add user message
    const userDisplay = { role: 'user', text };
    setDisplayMessages(prev => [...prev, userDisplay]);

    // Add empty assistant bubble — we'll stream into it
    const firstBubble = { role: 'assistant', text: '', streaming: true, toolStatuses: [] };
    setDisplayMessages(prev => [...prev, firstBubble]);

    setIsLoading(true);

    // Inbound: the patient's message goes to Ask Previu in English. The bubble
    // keeps what they typed, with the English beside it. If translation is
    // off or fails, the original is sent exactly as it always was — and the
    // reply is then left in English too (replyLang stays null).
    let apiText = text;
    let replyLang = null;
    if (language) {
      const inbound = await translate(text, 'in');
      if (inbound.disabled) translateOffRef.current = true;
      if (inbound.ok) {
        apiText = inbound.english || inbound.text;
        replyLang = language.replyLang;
      }
    }
    const translating = replyLang !== null;
    const nextBubbleId = () => `t${++bubbleSeqRef.current}`;
    let bubbleId = translating ? nextBubbleId() : null;
    if (translating) {
      const firstId = bubbleId;
      setDisplayMessages(prev => prev.map(m => {
        if (m === userDisplay) return { ...m, english: apiText, translationLang: language.lang };
        if (m === firstBubble) return { ...m, tid: firstId, awaitingTranslation: true };
        return m;
      }));
    }

    const userMsg = { role: 'user', content: apiText };
    const nextApiMessages = [...apiMessages, userMsg];
    setApiMessages(nextApiMessages);

    // Outbound: while translating, a bubble's English streams in hidden (the
    // typing cursor shows instead), and each bubble is translated once, when
    // it is finished — narration closed off before a tool, or the end of the
    // turn. bubbleText mirrors the bubble's text as onChunk builds it.
    let bubbleText = '';
    const pendingTranslations = [];
    const translateBubble = (tid, english) => {
      pendingTranslations.push(
        translate(english, 'out', replyLang).then(result => {
          if (result.disabled) translateOffRef.current = true;
          setDisplayMessages(prev => prev.map(m => (m.tid === tid ? withTranslation(m, english, result, replyLang) : m)));
        })
      );
    };

    // Track which steps (tool + action) have already been shown, to avoid duplicates
    const shownTools = new Set();
    // Capture marker_trend tool results for widget injection
    let capturedMarkerTrend = null;
    // Capture download_triage_report result for card injection
    let capturedTriageReportCard = null;
    // Capture create_order result for the lab-test payment card
    let capturedLabTestPaymentCard = null;
    // Capture create_appointment result for the appointment payment card
    let capturedAppointmentPaymentCard = null;

    try {
      const { finalMessages, truncated } = await sendChatMessage({
        messages: nextApiMessages,
        patient,
        triageContext: contextOverride ?? triageContextRef.current,

        onToolResult: ({ toolName, input, result }) => {
          console.log('[useChat] onToolResult', toolName, 'type=', result?.type);

          // Resolve the pill this step opened. Nothing did this before, so
          // every pill stayed in its running state — "Getting your panel
          // details…", dots bouncing, sitting above the answer built from
          // those very details.
          const outcome = result?.error ? 'error' : 'done';
          setDisplayMessages(prev =>
            prev.map(m => settleOneToolStatus(m, toolName, input?.action, outcome)));

          if (toolName === 'show_marker_trend' &&
              (result?.type === 'marker_trend' || result?.type === 'marker_trend_multi')) {
            // Keep the result with the most data — Claude may call tool multiple times;
            // don't let an empty result overwrite a good one.
            const newPoints = result?.dataPoints?.length ?? 0;
            const curPoints = capturedMarkerTrend?.dataPoints?.length ?? 0;
            if (!capturedMarkerTrend || newPoints >= curPoints) {
              capturedMarkerTrend = result;
              console.log('[useChat] capturedMarkerTrend set, type=', result.type,
                'dataPoints=', newPoints);
            }
          }
          if (toolName === 'download_triage_report' && result?.type === 'triage_report_card') {
            capturedTriageReportCard = result;
            console.log('[useChat] capturedTriageReportCard set, reportId=', result.reportId);
          }
          if (toolName === 'order_lab_test' && result?.type === 'lab_test_payment_card') {
            capturedLabTestPaymentCard = result;
            console.log('[useChat] capturedLabTestPaymentCard set, panelName=', result.panelName);
          }
          if (toolName === 'book_appointment' && result?.type === 'appointment_payment_card') {
            capturedAppointmentPaymentCard = result;
            console.log('[useChat] capturedAppointmentPaymentCard set, doctorName=', result.doctorName);
          }
        },

        onChunk: (chunk) => {
          if (translating) bubbleText = stripToolXml(bubbleText + chunk);
          setDisplayMessages(prev => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last?.role !== 'assistant') return next;

            const cleanedText = stripToolXml(last.text + chunk);

            next[next.length - 1] = { ...last, text: cleanedText, streaming: true };
            return next;
          });
        },

        onToolUse: ({ toolName, input }) => {
          // Deduped per ACTION, not per tool: one turn legitimately runs
          // several actions of the same tool (read the profile, read the saved
          // addresses, then save one), and each is its own step with its own
          // label. Keyed on the tool alone, only the first of them was ever
          // shown — and it was shown under whichever label that tool happened
          // to carry.
          const action  = input?.action;
          const stepKey = `${toolName}:${action ?? ''}`;
          if (shownTools.has(stepKey)) return;
          shownTools.add(stepKey);

          // Translating: narration kept below is a finished bubble, so it is
          // translated now; the post-tool answer gets a bubble of its own.
          // Only the narration text goes out — never the tool or its input.
          let closedNarration = null;
          if (translating) {
            if (isKeepableNarration(bubbleText)) {
              closedNarration = { tid: bubbleId, english: bubbleText.trim() };
              bubbleId = nextBubbleId();
            }
            bubbleText = '';
          }
          const openBubble = translating ? { tid: bubbleId, awaitingTranslation: true } : {};

          setDisplayMessages(prev => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last?.role !== 'assistant') return next;

            // Anything Claude said before reaching for the tool has already been
            // streamed into this bubble. It used to be blanked here, so the
            // patient watched a sentence appear and disappear a second later,
            // right before the answer arrived. Instead, close that bubble off as
            // a finished message and open a new one for the post-tool answer to
            // stream into — narration, then the pill, then the answer, and
            // nothing on screen is ever taken back.
            if (isKeepableNarration(last.text)) {
              next[next.length - 1] = { ...last, text: last.text.trim(), streaming: false };
              next.push({
                role: 'assistant', text: '', streaming: true,
                toolStatuses: [{ toolName, action, status: 'running' }],
                ...openBubble,
              });
              return next;
            }

            // Nothing worth keeping (empty, or "[Calling …]") — reuse the bubble.
            next[next.length - 1] = {
              ...last,
              text: '',
              toolStatuses: [
                ...(last.toolStatuses ?? []),
                { toolName, action, status: 'running' },
              ],
            };
            return next;
          });

          if (closedNarration) translateBubble(closedNarration.tid, closedNarration.english);
        },
      });

      // Snapshot capturedMarkerTrend NOW before calling setDisplayMessages.
      // The updater function runs asynchronously — if we reference the let variable
      // directly inside the updater, it may already be null by the time React
      // executes it (stale closure bug). Snapshotting into a const fixes this.
      const markerTrendSnapshot = capturedMarkerTrend;
      capturedMarkerTrend = null; // clear immediately — snapshot holds the ref
      const triageReportCardSnapshot = capturedTriageReportCard;
      capturedTriageReportCard = null;
      const labTestPaymentCardSnapshot = capturedLabTestPaymentCard;
      capturedLabTestPaymentCard = null;
      const appointmentPaymentCardSnapshot = capturedAppointmentPaymentCard;
      capturedAppointmentPaymentCard = null;

      // Translating: the last bubble is finished too. With no text (a turn
      // that ended on a tool) there is nothing to translate, so stop waiting.
      const finalEnglish = translating ? bubbleText.trim() : '';
      const finalBubble = translating && !finalEnglish ? { awaitingTranslation: false } : {};

      let finalAssistantText = '';
      setDisplayMessages(prev => {
        // The turn is over, so no tool can still be running. onToolResult
        // settles each pill as its tool returns; this catches the ones whose
        // result never came back through that path (a result that failed to
        // parse, an aborted tool loop) so nothing is left animating.
        const next = prev.map(m => settleToolStatuses(m));
        const last = next[next.length - 1];
        if (last?.role === 'assistant') {
          finalAssistantText = last.text;
          next[next.length - 1] = {
            ...last, streaming: false, ...finalBubble,
            // The reply hit the model's token limit — MessageBubble says so.
            ...(truncated && last.text ? { truncated: true } : {}),
          };
        }

        // Inject widget(s) in same batch as streaming-done
        if (markerTrendSnapshot) {
          const isMulti = markerTrendSnapshot.type === 'marker_trend_multi';

          if (isMulti) {
            // Inject up to 3 separate widget messages — one per marker with data.
            // Sort by data richness (most draws first) so best markers show first.
            const markersWithData = Object.entries(markerTrendSnapshot.results || {})
              .map(([name, r]) => ({ name, dataPoints: r.dataPoints || [], unit: r.unit || '' }))
              .filter(r => r.dataPoints.length > 0)
              .sort((a, b) => b.dataPoints.length - a.dataPoints.length)
              .slice(0, 3);

            console.log('[useChat] widget inject multi (batched):', markersWithData.map(m => m.name));

            for (const marker of markersWithData) {
              next.push({
                role:       'marker_trend',
                markerName: marker.name,
                unit:       marker.unit,
                data:       marker.dataPoints,
              });
            }
          } else {
            // Single marker
            const dataForWidget   = markerTrendSnapshot.dataPoints || [];
            const markerForWidget = markerTrendSnapshot.markerName;
            const unitForWidget   = markerTrendSnapshot.unit;

            console.log('[useChat] widget inject single (batched): marker=', markerForWidget,
              'dataPoints=', dataForWidget.length);

            if (dataForWidget.length > 0) {
              next.push({
                role:       'marker_trend',
                markerName: markerForWidget,
                unit:       unitForWidget,
                data:       dataForWidget,
              });
            }
          }
        }

        if (triageReportCardSnapshot) {
          console.log('[useChat] widget inject triage_report_card:', triageReportCardSnapshot.reportId);
          next.push({
            role:          'triage_report_card',
            patient:       triageReportCardSnapshot.patient,
            triageResult:  triageReportCardSnapshot.triageResult,
            triageHistory: triageReportCardSnapshot.triageHistory,
            reportId:      triageReportCardSnapshot.reportId,
            drawDate:      triageReportCardSnapshot.drawDate,
            riskLevel:     triageReportCardSnapshot.riskLevel,
            riskScore:     triageReportCardSnapshot.riskScore,
          });
        }

        if (labTestPaymentCardSnapshot) {
          console.log('[useChat] widget inject lab_test_payment_card:', labTestPaymentCardSnapshot.panelName);
          next.push({
            role:         'lab_test_payment_card',
            patient:      labTestPaymentCardSnapshot.patient,
            panelName:    labTestPaymentCardSnapshot.panelName,
            testCode:     labTestPaymentCardSnapshot.testCode,
            orderPayload: labTestPaymentCardSnapshot.orderPayload,
            summary:           labTestPaymentCardSnapshot.summary,
            savedAddresses:    labTestPaymentCardSnapshot.savedAddresses,
            selectedAddressId: labTestPaymentCardSnapshot.selectedAddressId,
          });
        }

        if (appointmentPaymentCardSnapshot) {
          console.log('[useChat] widget inject appointment_payment_card:', appointmentPaymentCardSnapshot.doctorName);
          next.push({
            role:                'appointment_payment_card',
            patient:             appointmentPaymentCardSnapshot.patient,
            doctorId:            appointmentPaymentCardSnapshot.doctorId,
            doctorName:          appointmentPaymentCardSnapshot.doctorName,
            appointmentType:     appointmentPaymentCardSnapshot.appointmentType,
            appointmentPayload:  appointmentPaymentCardSnapshot.appointmentPayload,
          });
        }

        return next;
      });
      // Sync full conversation (with tool_use + tool_result blocks) into apiMessages.
      // This is critical: wasToolCalled() in ChatService.filterToolsForContext() needs
      // to see tool_use blocks to know which tools have already been used.
      // Without this, get_panel_pricing is re-added to the schema every turn.
      if (finalMessages && finalMessages.length > 0) {
        setApiMessages(() => finalMessages);
      } else if (finalAssistantText) {
        // Fallback: at least record the assistant text
        setApiMessages(api => [
          ...api,
          { role: 'assistant', content: finalAssistantText },
        ]);
      }

      // capturedMarkerTrend is cleared before setDisplayMessages (see snapshot above).

      if (finalEnglish) translateBubble(bubbleId, finalEnglish);

    } catch (err) {
      setDisplayMessages(prev => {
        // Whatever was mid-flight when this blew up is not running any more.
        const next = prev.map(m => settleToolStatuses(m, 'error'));
        const last = next[next.length - 1];
        if (last?.role === 'assistant') {
          next[next.length - 1] = {
            ...last,
            streaming: false,
            text: last.text || 'Sorry, something went wrong. Please try again.',
            error: true,
            ...(translating ? { awaitingTranslation: false } : {}),
          };
        }
        return next;
      });
      setError(err.message);
    } finally {
      // Hold the input until every translated bubble has landed, so the next
      // message can't be sent ahead of this turn's reply. translate() settles
      // within 8 s and never rejects.
      if (pendingTranslations.length) await Promise.all(pendingTranslations);
      setIsLoading(false);
    }
  }, [apiMessages, isLoading, patient]);

  /**
   * stageUpload — called when the patient attaches a file via 📎. Doesn't
   * upload anything yet — shows a document_type_picker widget (blood test /
   * prescription / medical bill / x-ray / other) so the patient says what
   * the document actually is before it gets tagged and saved. Closes the
   * earlier mistagging gap (chat used to guess BLOOD_TEST vs OTHER purely
   * from file format) by asking instead of guessing.
   *
   * The File object itself lives in pendingFilesRef (keyed by pickerId)
   * until confirmUploadType() retrieves it — File objects aren't safe to
   * hold in React state (not serializable, and re-renders shouldn't be
   * carrying binary data around).
   */
  const stageUpload = useCallback((file) => {
    if (!file || !patient?.id) return;
    const pickerId = Date.now();
    pendingFilesRef.current.set(pickerId, file);
    // DEF-015 / TC-P015 — "Document type NOT auto-inferred on upload. User asked
    // to manually select." The picker stays (a human confirming the class of a
    // medical document is worth one tap), but it now arrives with the likely
    // answer already highlighted instead of five equal options. null means the
    // filename gave no signal, and nothing is pre-selected — better than
    // defaulting to BLOOD_TEST and having it accepted unread.
    const suggestedType = inferRecordTypeFromFileName(file.name);
    setDisplayMessages(prev => [
      ...prev,
      {
        role: 'document_type_picker',
        pickerId,
        fileName: file.name,
        fileSize: file.size,
        suggestedType,
        answered: false,
      },
    ]);
  }, [patient]);

  /**
   * confirmUploadType — called when the patient taps a type chip on the
   * document_type_picker widget. Runs the actual upload, then (only for
   * BLOOD_TEST PDFs) the auto-triage pipeline — same rule as the dedicated
   * /patient/upload page: only a PDF can go through client-side text
   * extraction (pdf.js — no OCR for images), so a non-PDF tagged BLOOD_TEST
   * still just saves without triage, same as that page's own behaviour.
   *
   * No content validation beyond the patient's own answer — there's no
   * server-side check that a PDF tagged BLOOD_TEST is actually a blood
   * panel. If Claude's extraction finds nothing lab-shaped in it,
   * /triage/cardiac just reports that honestly; the health_record still
   * saves either way.
   *
   * Defaults: drawDate = today — chat has no date field, unlike the
   * dedicated upload page. Flagging per FIGMA_TO_CODE_CHECKLIST's
   * "ambiguous values" rule — confirm this default is acceptable.
   */
  const confirmUploadType = useCallback(async (pickerId, recordType) => {
    const file = pendingFilesRef.current.get(pickerId);
    pendingFilesRef.current.delete(pickerId);
    if (!file || !patient?.id) return;

    // Mark the picker answered — MessageBubble hides the chips and shows
    // the chosen type instead, so the patient can't tap twice.
    setDisplayMessages(prev => prev.map(m =>
      m.role === 'document_type_picker' && m.pickerId === pickerId
        ? { ...m, answered: true, chosenType: recordType }
        : m
    ));

    const uploadId = Date.now();
    uploadIdRef.current = uploadId;
    setDisplayMessages(prev => [
      ...prev,
      { role: 'upload_status', uploadId, fileName: file.name, fileSize: file.size, stage: 'UPLOADING' },
    ]);

    const isPdf    = file.type === 'application/pdf';
    const drawDate = new Date().toISOString().slice(0, 10);

    let record;
    try {
      const res = await healthRecordAPI.upload(patient.id, file, { recordType, drawDate });
      record = res.data;
    } catch (e) {
      setDisplayMessages(prev => prev.map(m => m.uploadId === uploadId
        ? { ...m, stage: 'FAILED', errorText: e.response?.data?.message || 'Upload failed — please try again.' }
        : m));
      return;
    }

    // Auto-triage only runs for blood-test PDFs — everything else (images,
    // or a PDF tagged as something other than BLOOD_TEST) still saves
    // successfully; just no automatic AI analysis.
    const isPdfBloodTest = recordType === 'BLOOD_TEST' && (isPdf || isScanImage(file.type));
    if (!isPdfBloodTest) {
      setDisplayMessages(prev => prev.map(m => m.uploadId === uploadId ? { ...m, stage: 'SKIPPED' } : m));
      sendMessage(`I just uploaded a document (${file.name}).`);
      return;
    }

    uploadIdRef.current = uploadId;
    const triageResult = await runAutoTriage(record, file, patient, patient.id);
    if (!triageResult?.id) {
      // uploadStage/uploadError effect already marked the widget FAILED —
      // nothing further to do here.
      return;
    }

    // Reuse the exact same widget the download_triage_report chat tool
    // already renders (TriageReportCard via MessageBubble) — no new UI
    // needed for the result itself, only for the upload-progress state above.
    setDisplayMessages(prev => [
      ...prev,
      {
        role:          'triage_report_card',
        patient,
        triageResult,
        triageHistory: [],
        reportId:      `PRV-${String(patient?.id || '').slice(0, 8).toUpperCase()}-` +
          `${new Date(triageResult.createdAt || Date.now()).toISOString().slice(0, 10).replace(/-/g, '')}`,
        drawDate:      triageResult.drawDate || drawDate,
        riskLevel:     triageResult.consensusRiskLevel,
        riskScore:     triageResult.consensusRiskScore,
      },
    ]);

    // Fresh triageContext for THIS acknowledgement turn only — built from the
    // triage result that just landed, not the stale page-load welcomeSummary
    // (ChatPage's triageContext prop won't reflect this until its own
    // background refetch, which we don't wait on). Reuses the single
    // source-of-truth formatter so the wording matches every other surface.
    const contextOverride = buildTriageContext({
      lastResult: triageResult, openActionItems: 0, totalActionItems: 0,
      previuAge: null, previuAgeDelta: null,
    });
    sendMessage('I just uploaded a new lab report.', contextOverride);
  }, [patient, runAutoTriage, sendMessage]);

  const clearChat = useCallback(() => {
    setApiMessages([]);
    setDisplayMessages([]);
    setError(null);
  }, []);

  return {
    displayMessages, isLoading, error, sendMessage, stageUpload,
    confirmUploadType, clearChat,
  };
}
